import { AnalysisType } from '@analyzer/client';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Alert as UIAlert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAnalysisDestroyMutation, useAnalysisListQuery } from '../api/analysis.js';
import { formatDateUTC, timeToNow } from '../components/utils.js';

const levels = ['high', 'medium', 'low', 'info'] as const;
const colors: Record<(typeof levels)[number], string> = {
  high: '#FFB3BA',
  medium: '#FFDFBA',
  low: '#B4F0A7',
  info: '#BAE1FF'
};

type AnalysesListProps = {
  type: AnalysisType;
  projectId: string;
  resourceId: string;
};

export function AnalysisList({ type, projectId, resourceId }: AnalysesListProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: analyses,
    isLoading,
    isError,
    error
  } = useAnalysisListQuery(type, projectId, resourceId, {
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });
  const analysisDestroy = useAnalysisDestroyMutation(type);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (analysisId: string) => {
    analysisDestroy.mutate(
      { projectId, analysisId },
      {
        onSuccess: () => enqueueSnackbar('Analysis deleted', { variant: 'success' }),
        onError: (err) => enqueueSnackbar(`Failed to delete analysis: ${err.message}`, { variant: 'error' })
      }
    );
  };

  const deletingAnalysisId = analysisDestroy.variables?.analysisId;

  if (isLoading) return <CircularProgress />;
  if (isError) return <UIAlert severity="error">{`Error: ${error.message}`}</UIAlert>;

  analyses.sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime());

  return (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Alerts</TableCell>
            <TableCell>Snapshot</TableCell>
            <TableCell>Date (UTC)</TableCell>
            <TableCell>Timestamp</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {analyses.map((analysis) => (
            <TableRow key={analysis.id}>
              <TableCell>
                <Link to={`/projects/${projectId}/analyses/${type}/${analysis.id}`}>{analysis.id}</Link>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  {levels.map((severity) => (
                    <Chip
                      size="small"
                      key={severity}
                      label={`${analysis.alertCounts[severity]}`}
                      sx={{ backgroundColor: colors[severity], color: 'black' }}
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <Link to={`/projects/${projectId}/snapshots/${type}/${analysis.snapshotId}`}>
                  {analysis.snapshotId}
                </Link>
              </TableCell>
              <TableCell>{formatDateUTC(analysis.createdDate)}</TableCell>
              <TableCell>{timeToNow(analysis.createdDate)}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Delete analysis"
                  onClick={(event) => {
                    event.preventDefault();
                    handleDelete(analysis.id);
                  }}
                  disabled={analysisDestroy.isLoading && deletingAnalysisId === analysis.id}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              count={-1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton={true}
              showLastButton={true}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
